/**
 * Demo Data for Landing Page Components
 *
 * GUARDRAIL: This file contains static Georgian demo data only.
 * Demo components must NEVER import from hooks/, features/, or services/.
 * They may only import: UI primitives, icons, types, this file, LanguageContext, and utils.
 */

// Types for demo data (simplified versions of real types)
export interface DemoOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone?: string;
  address: string;
  parsed_items?: string;
  total_price?: number;
  order_status: string;
  created_at: string;
  internal_notes?: string;
  context_tag?: string;
  delivery_date?: string;
  actioned_today_at?: string | null;
  comments?: string; // Alert reason for Needs Attention
  session?: string; // Session assignment (courier name)
}

export interface DemoCourierOrder {
  customer_name: string;
  address: string;
  total_price?: number;
  showPrice: boolean;
  status: string;
  order_number?: string;
  customer_phone?: string;
}

export interface DemoPulseEvent {
  role: "manager" | "courier" | "system";
  orderNumber: string;
  message: string;
  timestamp: string;
  isDelivered?: boolean;
  isCritical?: boolean;
  isPaid?: boolean;
}

export interface DemoPlanningGroup {
  date: string;
  dateLabel: string;
  orders: {
    customer: string;
    product: string;
    price: number;
  }[];
}

export interface DemoLogEntry {
  role: "manager" | "courier" | "system";
  timestamp: string;
  message: string;
  isHighlight?: boolean;
  isSystem?: boolean;
}

export interface DemoCourierViewData {
  order: {
    customer_name: string;
    address: string;
    total_price: number;
    status: string;
    order_number: string;
  };
  lastManagerMessage: {
    message: string;
  };
  historyCount: number;
}

// ============================================
// LANGUAGE-AWARE DEMO DATA GETTERS
// ============================================

export type DemoTranslationFn = (key: string) => string;

// Get translated demo orders
export function getDemoOrders(t: DemoTranslationFn): DemoOrder[] {
  return [
    // Needs Attention (2) - overdue or requiring action
    {
      id: "demo-na-1",
      order_number: "OF-2401",
      customer_name: t("landing.demo.sampleNames.george"),
      customer_phone: "555 12 XX XX",
      address: t("landing.demo.sampleAddresses.vake"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.shirt")}","quantity":"2"},{"name":"${t("landing.demo.sampleProducts.jacket")}","quantity":"1"}]`,
      total_price: 185,
      order_status: "Ready",
      created_at: "2026-01-02T09:30:00Z",
      internal_notes: `[manager] · 09:30 · ${t("landing.demo.sampleLogs.orderReceived")}\n[manager] · 11:45 · ${t("landing.demo.sampleLogs.readyForDelivery")}`,
      context_tag: t("landing.demo.contextPriority"),
      comments: t("landing.demo.sampleAlerts.delayedDelivery"),
      actioned_today_at: null,
    },
    {
      id: "demo-na-2",
      order_number: "OF-2398",
      customer_name: t("landing.demo.sampleNames.anna"),
      customer_phone: "599 88 XX XX",
      address: t("landing.demo.sampleAddresses.isani"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.bag")}","quantity":"1"}]`,
      total_price: 95,
      order_status: "Delivery Failed",
      created_at: "2026-01-02T14:00:00Z",
      internal_notes: `[courier] · 16:30 · ${t("landing.demo.sampleAlerts.clientNotResponding")}`,
      comments: t("landing.demo.sampleAlerts.clientNotResponding"),
      actioned_today_at: null,
    },
    // Today Section (4) - actioned today
    {
      id: "demo-t-1",
      order_number: "OF-2402",
      customer_name: t("landing.demo.sampleNames.nino"),
      customer_phone: "577 45 XX XX",
      address: t("landing.demo.sampleAddresses.saburtalo"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.shoes")}","quantity":"1"}]`,
      total_price: 120,
      order_status: "Out for Delivery",
      created_at: "2026-01-03T10:15:00Z",
      internal_notes: `[courier] · 14:20 · ${t("landing.demo.sampleLogs.trafficDelay")}`,
      actioned_today_at: "2026-01-03T14:20:00Z",
      session: t("landing.demo.courier1"),
    },
    {
      id: "demo-t-2",
      order_number: "OF-2403",
      customer_name: t("landing.demo.sampleNames.david"),
      customer_phone: "598 11 XX XX",
      address: t("landing.demo.sampleAddresses.didube"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.watch")}","quantity":"1"}]`,
      total_price: 45,
      order_status: "Delivered",
      created_at: "2026-01-03T09:00:00Z",
      internal_notes: `[courier] · 15:10 · ${t("landing.demo.sampleLogs.leftWithNeighbor")}`,
      actioned_today_at: "2026-01-03T15:10:00Z",
      session: t("landing.demo.courier1"),
    },
    {
      id: "demo-t-3",
      order_number: "OF-2404",
      customer_name: t("landing.demo.sampleNames.mariam"),
      customer_phone: "555 99 XX XX",
      address: t("landing.demo.sampleAddresses.gldani"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.dress")}","quantity":"1"},{"name":"${t("landing.demo.sampleProducts.belt")}","quantity":"1"}]`,
      total_price: 75,
      order_status: "Ready",
      created_at: "2026-01-03T11:30:00Z",
      internal_notes: `[manager] · 12:00 · ${t("landing.demo.sampleLogs.warehousePreparing")}`,
      actioned_today_at: "2026-01-03T12:00:00Z",
      session: t("landing.demo.courier2"),
    },
    {
      id: "demo-t-4",
      order_number: "OF-2405",
      customer_name: t("landing.demo.sampleNames.levan"),
      customer_phone: "574 33 XX XX",
      address: t("landing.demo.sampleAddresses.nadzaladevi"),
      parsed_items: `[{"name":"${t("landing.demo.sampleProducts.coat")}","quantity":"1"}]`,
      total_price: 250,
      order_status: "Processing",
      created_at: "2026-01-03T13:45:00Z",
      internal_notes: `[manager] · 14:00 · ${t("landing.demo.sampleLogs.warehousePreparing")}`,
      actioned_today_at: "2026-01-03T14:00:00Z",
    },
  ];
}

// Get translated courier orders
export function getDemoCourierOrders(t: DemoTranslationFn): DemoCourierOrder[] {
  return [
    {
      customer_name: t("landing.demo.sampleNames.george"),
      address: t("landing.demo.sampleAddresses.vake"),
      total_price: 185,
      showPrice: true,
      status: "Ready",
      order_number: "OF-2401",
      customer_phone: "555 12 XX XX",
    },
    {
      customer_name: t("landing.demo.sampleNames.nino"),
      address: t("landing.demo.sampleAddresses.saburtalo"),
      total_price: 120,
      showPrice: true,
      status: "Out for Delivery",
      order_number: "OF-2402",
      customer_phone: "577 45 XX XX",
    },
    {
      customer_name: t("landing.demo.sampleNames.david"),
      address: t("landing.demo.sampleAddresses.didube"),
      total_price: 45,
      showPrice: true,
      status: "Delivered",
      order_number: "OF-2403",
      customer_phone: "598 11 XX XX",
    },
  ];
}

// Get translated pulse events
export function getDemoPulseEvents(t: DemoTranslationFn): DemoPulseEvent[] {
  return [
    {
      role: "system",
      orderNumber: "OF-2403",
      message: t("landing.demo.sampleLogs.paidCash"),
      timestamp: "15:12",
      isPaid: true,
    },
    {
      role: "courier",
      orderNumber: "OF-2403",
      message: t("landing.demo.delivered"),
      timestamp: "15:10",
      isDelivered: true,
    },
    {
      role: "manager",
      orderNumber: "OF-2401",
      message: t("landing.demo.sampleLogs.readyForDelivery"),
      timestamp: "11:45",
    },
    {
      role: "courier",
      orderNumber: "OF-2402",
      message: t("landing.demo.sampleLogs.inTransit"),
      timestamp: "14:20",
    },
  ];
}

// Get translated planning orders
export function getDemoPlanningOrders(t: DemoTranslationFn): DemoPlanningGroup[] {
  return [
    {
      date: "2026-01-04",
      dateLabel: t("landing.demo.tomorrow"),
      orders: [
        { customer: t("landing.demo.sampleNames.levan"), product: t("landing.demo.sampleProducts.bag"), price: 120 },
        { customer: t("landing.demo.sampleNames.mariam"), product: t("landing.demo.sampleProducts.dress"), price: 75 },
      ],
    },
    {
      date: "2026-01-05",
      dateLabel: "5 Jan",
      orders: [{ customer: t("landing.demo.sampleNames.tekla"), product: t("landing.demo.sampleProducts.coat"), price: 250 }],
    },
  ];
}

// Get translated log entries
export function getDemoLogEntries(t: DemoTranslationFn) {
  return {
    manager: [
      {
        role: "manager" as const,
        timestamp: "09:30",
        message: t("landing.demo.sampleLogs.orderReceived"),
      },
      {
        role: "manager" as const,
        timestamp: "11:45",
        message: t("landing.demo.sampleLogs.readyForDelivery"),
      },
      {
        role: "manager" as const,
        timestamp: "14:00",
        message: t("landing.demo.sampleLogs.handedToCourier"),
      },
    ],
    courier: [
      {
        role: "courier" as const,
        timestamp: "14:20",
        message: t("landing.demo.sampleLogs.confirmed"),
      },
      {
        role: "courier" as const,
        timestamp: "14:45",
        message: t("landing.demo.sampleLogs.inTransit"),
      },
      {
        role: "courier" as const,
        timestamp: "15:10",
        message: t("landing.demo.sampleLogs.deliveredCheck"),
        isHighlight: true,
      },
    ],
  };
}

// Get translated full conversation
export function getDemoFullConversation(t: DemoTranslationFn): DemoLogEntry[] {
  return [
    {
      timestamp: "10:32",
      message: t("landing.demo.sampleLogs.orderCreated"),
      role: "system",
      isSystem: true,
    },
    {
      timestamp: "10:45",
      message: t("landing.demo.sampleLogs.readyIn2Hours"),
      role: "manager",
    },
    {
      timestamp: "14:20",
      message: t("landing.demo.sampleLogs.pickedUpInVake"),
      role: "courier",
    },
    {
      timestamp: "14:22",
      message: t("landing.demo.sampleLogs.secondFloorApt8"),
      role: "manager",
    },
    {
      timestamp: "14:35",
      message: t("landing.demo.sampleLogs.deliveredPaidCash"),
      role: "courier",
      isHighlight: true,
    },
  ];
}

// Get translated courier view data
export function getDemoCourierView(t: DemoTranslationFn): DemoCourierViewData {
  return {
    order: {
      customer_name: t("landing.demo.sampleNames.george"),
      address: t("landing.demo.sampleAddresses.vake"),
      total_price: 185,
      status: "Out for Delivery",
      order_number: "OF-2401",
    },
    lastManagerMessage: {
      message: t("landing.demo.sampleLogs.secondFloorApt8"),
    },
    historyCount: 2,
  };
}

// ============================================
// STATIC FALLBACK DATA (Georgian - for backwards compatibility)
// ============================================
export const DEMO_ORDERS: DemoOrder[] = [
  {
    id: "demo-na-1",
    order_number: "OF-2401",
    customer_name: "გიორგი მამულაშვილი",
    customer_phone: "555 12 XX XX",
    address: "ვაკე, აბაშიძის 12",
    parsed_items: '[{"name":"პერანგი","quantity":"2"},{"name":"ქურთუკი","quantity":"1"}]',
    total_price: 185,
    order_status: "Ready",
    created_at: "2026-01-02T09:30:00Z",
    internal_notes: "[manager] · 09:30 · შეკვეთა მიღებულია\n[manager] · 11:45 · მზადაა მიტანისთვის",
    context_tag: "პრიორიტეტი",
    comments: "გადავადებული მიწოდება",
    actioned_today_at: null,
  },
  {
    id: "demo-na-2",
    order_number: "OF-2398",
    customer_name: "ანა გელაშვილი",
    customer_phone: "599 88 XX XX",
    address: "ისანი, ნავთლუღის 23",
    parsed_items: '[{"name":"ჩანთა","quantity":"1"}]',
    total_price: 95,
    order_status: "Delivery Failed",
    created_at: "2026-01-02T14:00:00Z",
    internal_notes: "[courier] · 16:30 · ვერ მივიტანე - არ იყო სახლში",
    comments: "კლიენტი არ პასუხობს",
    actioned_today_at: null,
  },
  {
    id: "demo-t-1",
    order_number: "OF-2402",
    customer_name: "ნინო ბერიძე",
    customer_phone: "577 45 XX XX",
    address: "საბურთალო, პეკინის 45",
    parsed_items: '[{"name":"ფეხსაცმელი","quantity":"1"}]',
    total_price: 120,
    order_status: "Out for Delivery",
    created_at: "2026-01-03T10:15:00Z",
    internal_notes: "[courier] · 14:20 · საცობია, 10 წუთში მივიტან",
    actioned_today_at: "2026-01-03T14:20:00Z",
    session: "კურიერი 1",
  },
  {
    id: "demo-t-2",
    order_number: "OF-2403",
    customer_name: "დავით კვარაცხელია",
    customer_phone: "598 11 XX XX",
    address: "დიდუბე, წერეთლის 78",
    parsed_items: '[{"name":"საათი","quantity":"1"}]',
    total_price: 45,
    order_status: "Delivered",
    created_at: "2026-01-03T09:00:00Z",
    internal_notes: "[courier] · 15:10 · კვარაცხელიას გადაეცით, მეზობელთან დავტოვე. თვითონ არ იყო სახლში",
    actioned_today_at: "2026-01-03T15:10:00Z",
    session: "კურიერი 1",
  },
  {
    id: "demo-t-3",
    order_number: "OF-2404",
    customer_name: "მარიამ თოფურია",
    customer_phone: "555 99 XX XX",
    address: "გლდანი, ხიზანიშვილის 5",
    parsed_items: '[{"name":"კაბა","quantity":"1"},{"name":"ქამარი","quantity":"1"}]',
    total_price: 75,
    order_status: "Ready",
    created_at: "2026-01-03T11:30:00Z",
    internal_notes: "[manager] · 12:00 · გიო, ხვალ დილით მზად იქნება მისატანად",
    actioned_today_at: "2026-01-03T12:00:00Z",
    session: "კურიერი 2",
  },
  {
    id: "demo-t-4",
    order_number: "OF-2405",
    customer_name: "ლევან გიორგაძე",
    customer_phone: "574 33 XX XX",
    address: "ნაძალადევი, გურამიშვილის 12",
    parsed_items: '[{"name":"პალტო","quantity":"1"}]',
    total_price: 250,
    order_status: "Processing",
    created_at: "2026-01-03T13:45:00Z",
    internal_notes: "[manager] · 14:00 · საწყობი ამზადებს შეკვეთას, ხვალ გაიარე",
    actioned_today_at: "2026-01-03T14:00:00Z",
  },
];

// Helper to get Needs Attention orders
export const DEMO_NEEDS_ATTENTION = DEMO_ORDERS.filter((o) => !o.actioned_today_at);

// Helper to get Today orders
export const DEMO_TODAY_ORDERS = DEMO_ORDERS.filter((o) => o.actioned_today_at);

// ============================================
// DEMO COURIER ORDERS (for phone mockup)
// ============================================
export const DEMO_COURIER_ORDERS: DemoCourierOrder[] = [
  {
    customer_name: "გიორგი მამულაშვილი",
    address: "ვაკე, აბაშიძის 12",
    total_price: 185,
    showPrice: true,
    status: "Ready",
    order_number: "OF-2401",
    customer_phone: "555 12 XX XX",
  },
  {
    customer_name: "ნინო ბერიძე",
    address: "საბურთალო, პეკინის 45",
    total_price: 120,
    showPrice: true,
    status: "Out for Delivery",
    order_number: "OF-2402",
    customer_phone: "577 45 XX XX",
  },
  {
    customer_name: "დავით კვარაცხელია",
    address: "დიდუბე, წერეთლის 78",
    total_price: 45,
    showPrice: true,
    status: "Delivered",
    order_number: "OF-2403",
    customer_phone: "598 11 XX XX",
  },
];

// ============================================
// DEMO LOG ENTRIES (Georgian-only)
// ============================================
export const DEMO_LOG_ENTRIES: {
  manager: DemoLogEntry[];
  courier: DemoLogEntry[];
} = {
  manager: [
    {
      role: "manager",
      timestamp: "09:30",
      message: "შეკვეთა მიღებულია",
    },
    {
      role: "manager",
      timestamp: "11:45",
      message: "მზადაა მიტანისთვის",
    },
    {
      role: "manager",
      timestamp: "14:00",
      message: "კურიერს გადაეცა",
    },
  ],
  courier: [
    {
      role: "courier",
      timestamp: "14:20",
      message: "დადასტურებულია",
    },
    {
      role: "courier",
      timestamp: "14:45",
      message: "გზაშია",
    },
    {
      role: "courier",
      timestamp: "15:10",
      message: "ჩაბარდა ✓",
      isHighlight: true,
    },
  ],
};

// Combined log for single view
export const DEMO_COMBINED_LOG: DemoLogEntry[] = [
  ...DEMO_LOG_ENTRIES.manager,
  ...DEMO_LOG_ENTRIES.courier,
  {
    role: "system",
    timestamp: "15:10",
    message: "მიტანილია",
    isHighlight: true,
  },
];

// ============================================
// DEMO PULSE EVENTS (for LivePulse strip)
// ============================================
export const DEMO_PULSE_EVENTS: DemoPulseEvent[] = [
  {
    role: "system",
    orderNumber: "OF-2403",
    message: "ნაღდით გადაიხადა",
    timestamp: "15:12",
    isPaid: true,
  },
  {
    role: "courier",
    orderNumber: "OF-2403",
    message: "ჩაბარდა",
    timestamp: "15:10",
    isDelivered: true,
  },
  {
    role: "manager",
    orderNumber: "OF-2401",
    message: "მზადაა მიტანისთვის",
    timestamp: "11:45",
  },
  {
    role: "courier",
    orderNumber: "OF-2402",
    message: "გზაშია",
    timestamp: "14:20",
  },
];

// ============================================
// DEMO PLANNING ORDERS (for Planning Radar)
// ============================================
export const DEMO_PLANNING_ORDERS: DemoPlanningGroup[] = [
  {
    date: "2026-01-04",
    dateLabel: "ხვალ",
    orders: [
      { customer: "ლევან გიორგაძე", product: "ჩანთა", price: 120 },
      { customer: "მარიამ თოფურია", product: "კაბა", price: 75 },
    ],
  },
  {
    date: "2026-01-05",
    dateLabel: "5 იანვარი",
    orders: [{ customer: "თეკლა ჯავახიშვილი", product: "პალტო", price: 250 }],
  },
];

// ============================================
// STATUS DISPLAY HELPERS
// ============================================
export const STATUS_COLORS: Record<string, { bg: string; text: string; rowBg?: string }> = {
  New: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-300",
    rowBg: "bg-blue-50/50 dark:bg-blue-900/10",
  },
  Processing: {
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-700 dark:text-yellow-300",
    rowBg: "bg-yellow-50/50 dark:bg-yellow-900/10",
  },
  Ready: {
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-700 dark:text-emerald-300",
    rowBg: "bg-emerald-50/50 dark:bg-emerald-900/10",
  },
  "Out for Delivery": {
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-700 dark:text-purple-300",
    rowBg: "bg-purple-50/50 dark:bg-purple-900/10",
  },
  Delivered: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-300",
    rowBg: "bg-green-50/50 dark:bg-green-900/10",
  },
  "Delivery Failed": {
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-300",
    rowBg: "bg-red-50/50 dark:bg-red-900/10",
  },
  Cancelled: {
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-600 dark:text-gray-400",
    rowBg: "bg-gray-50/50 dark:bg-gray-800/10",
  },
};

// Status labels for Georgian (backwards compatibility)
export const STATUS_LABELS_KA: Record<string, string> = {
  New: "ახალი",
  Processing: "მუშავდება",
  Ready: "მზადაა",
  "Out for Delivery": "გზაშია",
  Delivered: "მიტანილია",
  "Delivery Failed": "ვერ მოხერხდა",
  Cancelled: "გაუქმებულია",
};

// ============================================
// DEMO FULL CONVERSATION (Manager View - SharedLog)
// ============================================
export const DEMO_FULL_CONVERSATION: DemoLogEntry[] = [
  {
    timestamp: "10:32",
    message: "შეკვეთა შეიქმნა",
    role: "system",
    isSystem: true,
  },
  {
    timestamp: "10:45",
    message: "მაკომ 2 საათზე იქნება მზადო, საწყობში",
    role: "manager",
  },
  {
    timestamp: "14:20",
    message: "ავიღე, ვაკეში ვარ უკვე",
    role: "courier",
  },
  {
    timestamp: "14:22",
    message: "კარგი, მეორე სართულზე უნდა დატოვო, ბინა 8",
    role: "manager",
  },
  {
    timestamp: "14:35",
    message: "ჩაბარებულია, ნაღდით გადაიხადა",
    role: "courier",
    isHighlight: true,
  },
];

// ============================================
// DEMO COURIER VIEW (Courier View - SharedLog)
// ============================================
export const DEMO_COURIER_VIEW: DemoCourierViewData = {
  order: {
    customer_name: "გიორგი მამულაშვილი",
    address: "ვაკე, აბაშიძის 12",
    total_price: 185,
    status: "Out for Delivery",
    order_number: "OF-2401",
  },
  lastManagerMessage: {
    message: "კარგი, მეორე სართულზე უნდა დატოვო, ბინა 8",
  },
  historyCount: 2,
};
